import { createInstance } from '@/helpers/api/base'
import { useAppMutation } from '@/helpers/api/mutation'
import { DisruptionFormValues } from '@/pages/Disruptions/types'
import { useDisruptionsTableFormStateStore } from '../store'
import { useNotification } from '@/ui/Notification'
import { Disruption } from '@/types/disruptions'

interface IUpdateDisruptionRequest
    extends Omit<DisruptionFormValues, 'routes' | 'diversionRoutes' | 'delayInMinutes' | 'nmccInformed'> {
    routes: string[]
    diversionRoutes: { diversion: string | null; route: string }[]
    reference: string
    nmccInformed: boolean | null
    delayInMinutes: number | null
}

export const useUpdateDisruption = () => {
    const { clearInputValues } = useDisruptionsTableFormStateStore()
    const { showNotification } = useNotification()

    const updateDisruptionMutation = useAppMutation<
        void,
        Error,
        IUpdateDisruptionRequest & { _disruptionId: string },
        Disruption[]
    >({
        mutationFn: ({ _disruptionId, ...data }) =>
            createInstance({
                url: `/disruptions/${_disruptionId}`,
                method: 'PUT',
                data,
            }),
        alertOnError: true,
        queryKey: ['GET_DISRUPTIONS'],
        optimisticChangesCallback: (
            requestBody: IUpdateDisruptionRequest & { _disruptionId: string },
            previousDisruptions?: Disruption[]
        ) => {
            return previousDisruptions?.map((item) =>
                item.reference === requestBody.reference
                    ? {
                          ...item,
                          status: requestBody.status,
                          routes: requestBody.diversionRoutes.filter((dr) => requestBody.routes.includes(dr.route)),
                          location: requestBody.location,
                          diversion: requestBody.details,
                          disruptionType: requestBody.disruptionType,
                          delay: requestBody.delayInMinutes,
                          nmccInformed: requestBody.nmccInformed,
                          start: requestBody.start,
                          end: requestBody.end,
                      }
                    : item
            )
        },
        onSuccess: async () => {
            clearInputValues()
            showNotification({ message: 'Disruption updated' })
        },
    })

    const handleUpdateDisruption = (
        data: DisruptionFormValues,
        selectedRowId: string | null,
        selectedRoutes: string[]
    ) => {
        if (!data.reference) throw new Error('Reference not found')
        if (!selectedRowId) throw new Error('Disruption ID not found')

        const nonEmptyDiversionRoutes = data.diversionRoutes
            .filter((route) => route.route)
            .map((route) => ({
                diversion: route.diversion === '' ? null : route.diversion,
                route: route.route,
            }))

        const emptyDiversionRoutes = selectedRoutes
            .filter((route) => !nonEmptyDiversionRoutes.map((route) => route.route).includes(route))
            .map((route) => ({
                diversion: null,
                route,
            }))

        const allDiversionRoutes = [...nonEmptyDiversionRoutes, ...emptyDiversionRoutes]

        updateDisruptionMutation.mutate({
            _disruptionId: selectedRowId,
            status: data.status,
            reference: data.reference,
            routes: selectedRoutes,
            diversionRoutes:
                selectedRoutes.length === 1
                    ? [
                          {
                              diversion:
                                  data.diversionRoutes[0].diversion === '' ? null : data.diversionRoutes[0].diversion,
                              route: selectedRoutes[0],
                          },
                      ]
                    : allDiversionRoutes,
            location: data.location,
            details: data.details,
            disruptionType: data.disruptionType,
            delayInMinutes: data.delayInMinutes ? Number(data.delayInMinutes) : null,
            nmccInformed: data.nmccInformed === 'Yes' ? true : data.nmccInformed === 'No' ? false : null,
            start: data.start,
            end: data.end,
        })
    }

    return { handleUpdateDisruption, isPending: updateDisruptionMutation.isPending }
}
